import { NavLink } from "react-router-dom";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "./ui/NavigationMenu.jsx";
import { cn } from "../lib/utils.js";
import { usePendingCount } from "../hooks/usePendingCount.js";
import { useAuth } from "../context/AuthContext.jsx";
import { useNavBadgeCounts } from "../context/NavBadgeCountsContext.jsx";
import {
  badgeValue,
  buildGestionItems,
  NAV_BADGE_LABELS,
  REPORTES_GROUPS,
  sumGroupBadges,
} from "../lib/navConfig.js";
import { buildProfileNav } from "../lib/profileNav.js";
import { isMember } from "../lib/roles.js";
import { isNative } from "../lib/platform.js";
import { CountBadge, LiveDot, ThemeToggle } from "./ui";

// ─── Nav data ──────────────────────────────────────────────────────────────────

// El desplegable "Control" se fue al rail de /admin (sección Control):
// comandos, geocercas y la configuración de alertas son ajustes de la flota,
// no consulta diaria, y allá tienen buscador y agrupación. El cuarto destino
// del menú, "Puntos de interés", apuntaba a una ruta que nunca existió.

// El árbol de Reportes (y el contador que cuelga de "Historial de alertas")
// vive en lib/navConfig.js: la pantalla /reportes del móvil dibuja los mismos
// destinos y tiene que mostrar el mismo globo.

// ─── MenuItem — unified size for all dropdowns ────────────────────────────────
// Icon wrapper p-1 / 14px icon, título en text-sm y nada más: la `description`
// que traen los destinos en lib/navConfig.js es para las tarjetas de /reportes
// en el teléfono, no para una columna de 150px.

function MenuItem({ title, icon, href, badge, counts }) {
  const count = badgeValue(badge, counts);

  return (
    <li>
      <NavigationMenuLink asChild>
        <NavLink
          to={href}
          className="group flex gap-2.5 items-start rounded-lg p-2 hover:bg-surface-1/70 transition-colors outline-none"
        >
          {/* En hover la pastilla se INVIERTE contra el fondo. El icono no
              puede ser un color fijo: tiene que ser el del fondo de la app,
              o en tema claro quedaba tinta oscura sobre pastilla oscura. */}
          <div className="border border-border rounded-md p-1 shrink-0 group-hover:bg-text-primary group-hover:border-text-primary transition-all duration-150">
            <span
              className="material-symbols-outlined text-[14px] text-text-dim group-hover:text-background-elevated transition-colors"
              aria-hidden="true"
            >
              {icon}
            </span>
          </div>
          <div className="flex min-w-0 flex-1 items-start gap-2">
            <p className="text-sm font-medium text-text-secondary group-hover:text-text-primary transition-colors leading-none">
              {title}
            </p>
            <CountBadge
              count={count}
              label={NAV_BADGE_LABELS[badge]}
              className="ml-auto"
            />
          </div>
        </NavLink>
      </NavigationMenuLink>
    </li>
  );
}

// ─── ReportesContent — mega-menu with grouped columns ─────────────────────────

function ReportesContent({ counts }) {
  return (
    <div className="p-3 w-[min(95vw,32rem)]">
      <div className="grid grid-cols-3 gap-3">
        {REPORTES_GROUPS.map((group) => (
          <div key={group.label}>
            {/* Group header */}
            <div className="flex items-center gap-1.5 mb-2 pb-1.5 border-b border-border-muted">
              <span
                className="material-symbols-outlined text-[12px] text-text-muted"
                aria-hidden="true"
              >
                {group.icon}
              </span>
              <span className="text-2xs font-bold text-text-muted uppercase tracking-widest">
                {group.label}
              </span>
            </div>

            {/* Group items — MenuItem dibuja solo título + globo */}
            <ul className="space-y-0.5">
              {group.items.map((item) => (
                <MenuItem
                  key={item.href}
                  title={item.title}
                  href={item.href}
                  icon={item.icon}
                  badge={item.badge}
                  counts={counts}
                />
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Shared trigger style ──────────────────────────────────────────────────────

const triggerCn = cn(
  navigationMenuTriggerStyle(),
  "relative flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors bg-transparent text-text-dim hover:text-text-primary hover:bg-surface-1/60 data-[state=open]:text-text-primary data-[state=open]:bg-surface-1 h-auto",
);

// ─── TopNav ───────────────────────────────────────────────────────────────────

export default function TopNav({ wsConnected }) {
  // Las alertas vienen del contexto; los pendientes siguen saliendo del hook,
  // que es la fuente única y sirve también al rail de /admin, que se dibuja
  // fuera de este proveedor. Ver context/NavBadgeCountsContext.jsx.
  const { alerts } = useNavBadgeCounts();
  const pendingCount = usePendingCount();
  const { logout, user } = useAuth();
  const role = user?.role;
  const GESTION = buildGestionItems(role);
  const PROFILE_SHORTCUTS = buildProfileNav(role);
  const showDashboard = !isMember(user);
  const showGestionMenu = GESTION.length > 0;
  const reportesCounts = { alerts };
  const reportesBadge = sumGroupBadges(REPORTES_GROUPS, reportesCounts);

  return (
    <>
      {/*
        En la app nativa el WebView dibuja bajo la barra de estado (Android 15
        fuerza edge-to-edge y con targetSdk 36 ya no hay opt-out), así que la
        barra reserva ese alto con pt-[env(safe-area-inset-top)] y su propio
        bg-background-dark pinta la franja: se ve como una sola pieza con el
        reloj del sistema encima.

        El pb existe para que el contenido no quede descolgado. La barra de
        estado de Android mide 24 dp de contenido, pero el inset es más alto
        cuando hay recorte de pantalla: en el Redmi Note 13 Pro los iconos del
        sistema terminan en y=74 y el inset llega a y=122, porque ahí se reserva
        el despeje de la cámara. Ese sobrante (`inset - 24dp`) cae ARRIBA del
        logo y del avatar, y sin nada abajo la barra se ve caída.

        Va la MITAD del sobrante y no el sobrante entero: compensarlo completo
        centra el contenido pero engorda la franja de arriba, que es lo que se
        quería achicar. La mitad equilibra sin pagar el alto. En un teléfono sin
        recorte el inset ES 24dp, la cuenta da 0 y la barra queda pegada — que es
        justo lo que corresponde: ahí no sobra nada que compensar.

        Alto: ninguno. La barra mide el inset más su contenido más ese pb; no
        hay un número elegido a dedo. En web env() vale 0 y sigue en h-14.
      */}
      <nav
        data-shell-nav-anchor
        className={cn(
          "bg-background-elevated sheen-chrome shadow-card border-b border-border-muted flex items-center px-4 gap-1 shrink-0 z-[300] relative",
          isNative()
            ? "pt-[env(safe-area-inset-top)] pb-[calc((env(safe-area-inset-top)-1.5rem)/2)]"
            : "h-14",
        )}
      >
        {/*
          Ranura del menú del shell en móvil. La llena por portal el
          <MobileNavMenu/> que monte la pantalla activa (/admin, /perfil): el
          contenido es de la ruta, pero el ☰ va acá arriba, que es donde se
          busca un menú en un teléfono. Vacía no ocupa nada.
        */}
        <div
          id="shell-nav-slot"
          className="peer flex shrink-0 items-center empty:hidden md:hidden"
        />

        {/*
          Logo — en móvil le cede el sitio al ☰ cuando la ranura de al lado
          está llena (`peer-[:not(:empty)]`). En desktop siempre está: el rail
          de cada shell ya vive en la columna izquierda, la ranura no se usa.
        */}
        <div className="flex shrink-0 items-center gap-2 max-md:peer-[:not(:empty)]:hidden md:mr-4">
          <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center shrink-0">
            {/* `text-white` y no `text-text-primary`: la pastilla es un relleno
                de marca (bg-primary), y sobre marca el par correcto es el
                blanco en los DOS temas — `text-primary` se vuelve tinta oscura
                en claro y dejaría el icono casi negro sobre azul. */}
            <span
              className="material-symbols-outlined text-[16px] text-white"
              aria-hidden="true"
            >
              satellite_alt
            </span>
          </div>
          <span className="text-text-primary font-bold text-sm tracking-wide hidden lg:block">
            GPS Platform
          </span>
        </div>

        {/* ── Nav links — desktop only ── */}
        <div className="hidden md:flex items-center gap-0.5 flex-1">
          {/* Dashboard — hidden for viewer (no dashboard surface) */}
          {showDashboard && (
            <NavLink
              to="/dashboard"
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors",
                  isActive
                    ? "text-text-primary bg-surface-1"
                    : "text-text-dim hover:text-text-primary hover:bg-surface-1/60",
                )
              }
            >
              <span
                className="material-symbols-outlined text-[15px]"
                aria-hidden="true"
              >
                home
              </span>
              Dashboard
            </NavLink>
          )}

          {/* Monitor */}
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors",
                isActive
                  ? "text-text-primary bg-surface-1"
                  : "text-text-dim hover:text-text-primary hover:bg-surface-1/60",
              )
            }
          >
            <span
              className="material-symbols-outlined text-[15px]"
              aria-hidden="true"
            >
              map
            </span>
            Monitor
          </NavLink>

          {/* Dropdowns */}
          <NavigationMenu>
            <NavigationMenuList>
              {/* Reportes ▾ */}
              <NavigationMenuItem>
                <NavigationMenuTrigger className={cn(triggerCn)}>
                  <span
                    className="material-symbols-outlined text-[15px]"
                    aria-hidden="true"
                  >
                    bar_chart
                  </span>
                  Reportes
                  {/*
                    El número es la SUMA del árbol y el rótulo está clavado en
                    una clave: se sostiene porque "alerts" es la única que el
                    árbol puede declarar, y eso lo ancla un test (ver el ítem
                    "reportes" en lib/navConfig.js). Si el árbol gana una
                    segunda clave, este rótulo miente antes que el número.
                  */}
                  <CountBadge
                    count={reportesBadge}
                    label={NAV_BADGE_LABELS.alerts}
                    className="absolute -top-0.5 -right-0.5"
                  />
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ReportesContent counts={reportesCounts} />
                </NavigationMenuContent>
              </NavigationMenuItem>

              {/*
                PR-B: the legacy "Gestión" dropdown collapses into a single
                "Administración" link that points at the consolidated /admin
                shell. Visibility is gated on the same buildGestionItems
                signal as before so viewers (zero items) still see nothing.
              */}
              {showGestionMenu && (
                <NavLink
                  to="/admin"
                  className={({ isActive }) =>
                    cn(
                      "relative flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors",
                      isActive
                        ? "text-text-primary bg-surface-1"
                        : "text-text-dim hover:text-text-primary hover:bg-surface-1/60",
                    )
                  }
                >
                  <span
                    className="material-symbols-outlined text-[15px]"
                    aria-hidden="true"
                  >
                    folder_managed
                  </span>
                  Administración
                  <CountBadge
                    count={pendingCount}
                    label={NAV_BADGE_LABELS.pending}
                    className="absolute -top-0.5 -right-0.5"
                  />
                </NavLink>
              )}
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        {/* ── Right side ── */}
        <div className="flex items-center gap-1 ml-auto">
          {/* Always-visible WS status indicator — color dot with tooltip.
              Complements the red banner (which only shows on disconnect) so
              users always know whether real-time is live. */}
          <div
            className="hidden sm:flex items-center gap-1.5 px-2 py-1 rounded-full bg-surface-1/60 border border-border-muted mr-1"
            title={
              wsConnected
                ? "Conexión en tiempo real activa"
                : "Sin conexión en tiempo real — reconectando"
            }
            role="status"
            aria-live="polite"
          >
            <LiveDot
              color={wsConnected ? "success" : "danger"}
              animate={wsConnected}
              size={8}
              className="shrink-0"
            />
            <span
              className={cn(
                "text-xs font-medium hidden md:inline",
                wsConnected ? "text-success" : "text-danger",
              )}
            >
              {wsConnected ? "En vivo" : "Offline"}
            </span>
          </div>

          <DropdownMenuPrimitive.Root>
            <DropdownMenuPrimitive.Trigger asChild>
              <button
                type="button"
                aria-label="Mi cuenta"
                className="w-8 h-8 rounded-full bg-surface-1 border border-border flex items-center justify-center hover:bg-surface-2 transition-colors ml-0.5 focus:outline-none"
              >
                <span
                  className="material-symbols-outlined text-[18px] text-text-secondary"
                  aria-hidden="true"
                >
                  person
                </span>
              </button>
            </DropdownMenuPrimitive.Trigger>

            <DropdownMenuPrimitive.Portal>
              <DropdownMenuPrimitive.Content
                align="end"
                sideOffset={8}
                className="z-[400] min-w-[min(90vw,14rem)] bg-background-card sheen-2 border border-border-muted rounded-xl shadow-elevated p-1.5"
              >
                {(user?.fullName || user?.email) && (
                  <>
                    <div className="px-3 py-2">
                      {user.fullName && (
                        <p className="text-sm font-semibold text-text-primary truncate leading-tight">
                          {user.fullName}
                        </p>
                      )}
                      {user.email && (
                        <p className="text-xs text-text-muted truncate mt-0.5">
                          {user.email}
                        </p>
                      )}
                    </div>
                    <DropdownMenuPrimitive.Separator className="my-1 h-px bg-surface-1" />
                  </>
                )}

                {PROFILE_SHORTCUTS.map((item) => {
                  return (
                    <DropdownMenuPrimitive.Item asChild key={item.path}>
                      <NavLink
                        to={`/perfil?tab=${item.sectionId}`}
                        className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-1/60 rounded-lg cursor-pointer outline-none transition-colors"
                      >
                        <span
                          className="material-symbols-outlined text-[16px]"
                          aria-hidden="true"
                        >
                          {item.icon}
                        </span>
                        {item.label}
                      </NavLink>
                    </DropdownMenuPrimitive.Item>
                  );
                })}

                <DropdownMenuPrimitive.Separator className="my-1.5 h-px bg-surface-1" />

                {/* El selector NO va envuelto en DropdownMenu.Item: un Item
                    captura las flechas para su propio recorrido y cierra el
                    menú al soltar la tecla, y este control ES un grupo de radio
                    que se maneja justo con flechas. Fuera del Item el grupo
                    conserva su navegación y el menú no se cierra al elegir —
                    que además es lo que se quiere: el cambio se ve al instante
                    y ahí mismo se puede corregir.

                    Se frenan SOLO las flechas, no toda tecla: Radix cierra el
                    menú escuchando Escape en su Content, así que un
                    stopPropagation a secas dejaba el menú sin forma de cerrarse
                    con el teclado justo mientras el foco estaba en el selector
                    — que es el público al que sirve el grupo de radio nativo. */}
                <div
                  className="px-3 py-2"
                  onKeyDown={(e) => {
                    if (e.key.startsWith("Arrow")) e.stopPropagation();
                  }}
                >
                  <p
                    id="tema-menu-label"
                    className="text-2xs font-bold text-text-muted uppercase tracking-[0.08em] mb-1.5"
                  >
                    Tema
                  </p>
                  <ThemeToggle labelledBy="tema-menu-label" compact />
                </div>

                <DropdownMenuPrimitive.Separator className="my-1.5 h-px bg-surface-1" />

                <DropdownMenuPrimitive.Item asChild>
                  <button
                    type="button"
                    onClick={logout}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-danger hover:text-danger hover:bg-danger/10 rounded-lg cursor-pointer outline-none transition-colors"
                  >
                    <span
                      className="material-symbols-outlined text-[16px]"
                      aria-hidden="true"
                    >
                      logout
                    </span>
                    Cerrar sesión
                  </button>
                </DropdownMenuPrimitive.Item>

                <DropdownMenuPrimitive.Separator className="my-1.5 h-px bg-surface-1" />

                <div className="flex items-center gap-2 px-3 py-2">
                  <LiveDot
                    color={wsConnected ? "success" : "danger"}
                    animate={wsConnected}
                    size={8}
                    className="shrink-0"
                  />
                  <span
                    className={cn(
                      "text-xs",
                      wsConnected ? "text-success" : "text-danger",
                    )}
                  >
                    {wsConnected ? "En línea" : "Desconectado"}
                  </span>
                </div>
              </DropdownMenuPrimitive.Content>
            </DropdownMenuPrimitive.Portal>
          </DropdownMenuPrimitive.Root>
        </div>
      </nav>
    </>
  );
}
